// Vanuatu National Syllabus: Economics - Senior Secondary (Years 11-13)
// Based on Y11-13_Economics_syl_RevEQAP&CDU_Feb2023_RR_WEB.pdf

export const economicsCurriculum = {
  "Year 11": {
    courseInfo: {
      subject: "Economics",
      level: "Year 11",
      school: "St Patrick's College",
      assessmentWeighting: {
        internalExamination: 60,
        continuousAssessment: 40
      },
      totalWeeks: 40,
      periodsPerWeek: 4
    },
    strands: [
      {
        id: "y11-eco-1",
        name: "Resource Allocation through the Market System",
        code: "ECO1",
        weighting: 19,
        majorLearningOutcome: "Demonstrate understanding of how resources are allocated through the market system",
        subStrands: [
          {
            id: "ECO1.1",
            title: "Introduction to Economics",
            description: "Understanding the basic economic problem and fundamental concepts",
            slos: [
              "Define the economic problem in terms of scarcity, choice, and opportunity cost",
              "Identify and explain the factors of production and their rewards",
              "Distinguish between goods and services",
              "Explain specialization and interdependence in an economy",
              "Identify the three sectors of the economy (primary, secondary, tertiary)",
              "Draw and interpret the Production Possibility Curve (PPC)",
              "Explain the implications of points inside, on, and outside the PPC"
            ]
          },
          {
            id: "ECO1.2",
            title: "Consumers' Decisions and Demand",
            description: "Understanding consumer behavior and the law of demand",
            slos: [
              "Define demand and explain the law of demand",
              "Distinguish between individual demand and market demand",
              "Derive market demand from individual demand curves",
              "Identify price and non-price factors affecting demand",
              "Draw and interpret the demand curve",
              "Explain movements along vs shifts of the demand curve",
              "Classify goods as luxury, necessity, or inferior goods",
              "Define and calculate price elasticity of demand"
            ]
          },
          {
            id: "ECO1.3",
            title: "Producers' Decisions and Supply",
            description: "Understanding producer behavior and the law of supply",
            slos: [
              "Define supply and explain the law of supply",
              "Distinguish between individual supply and market supply",
              "Derive market supply from individual supply curves",
              "Identify price and non-price factors affecting supply",
              "Draw and interpret the supply curve",
              "Explain movements along vs shifts of the supply curve",
              "Explain economies and diseconomies of scale",
              "Describe the role of technology and specialization in production"
            ]
          },
          {
            id: "ECO1.4",
            title: "The Market I",
            description: "Understanding market structures and equilibrium",
            slos: [
              "Define and explain different market structures (perfect, imperfect competition)",
              "Identify features of monopoly, monopolistic competition, oligopoly, duopoly",
              "Determine market equilibrium price and quantity",
              "Analyze excess supply and demand situations",
              "Explain the effects of price controls (minimum and maximum prices)",
              "Analyze the effects of quotas, subsidies, and taxes on markets"
            ]
          },
          {
            id: "ECO1.5",
            title: "Investigations",
            description: "Applying economic research skills",
            slos: [
              "Formulate research objectives and hypotheses",
              "Collect and organize economic data",
              "Process, interpret, and analyze economic data",
              "Draw conclusions and make recommendations"
            ]
          }
        ],
        topics: [
          { name: "The Economic Problem", weeks: "1-2", description: "Scarcity, choice, opportunity cost" },
          { name: "Factors of Production", weeks: "3-4", description: "Land, labor, capital, enterprise" },
          { name: "Demand Analysis", weeks: "5-7", description: "Law of demand, elasticity, demand curves" },
          { name: "Supply Analysis", weeks: "8-10", description: "Law of supply, production, supply curves" },
          { name: "Market Equilibrium", weeks: "11-13", description: "Price determination, market structures" }
        ]
      },
      {
        id: "y11-eco-2",
        name: "Resource Allocation through the Public Sector",
        code: "ECO2",
        weighting: 16,
        majorLearningOutcome: "Demonstrate understanding of how resources are allocated through government intervention",
        subStrands: [
          {
            id: "ECO2.1",
            title: "Government Intervention I",
            description: "Understanding the role of government in the economy",
            slos: [
              "Explain reasons for government intervention in the economy",
              "Describe the economic functions of government",
              "Identify the economic aims of government (growth, employment, stability)",
              "Describe sources of government revenue",
              "Classify types of taxes (direct, indirect)",
              "Explain different tax systems (progressive, regressive, proportional)",
              "Analyze the effects of taxes on different groups"
            ]
          },
          {
            id: "ECO2.2",
            title: "Classifications of Goods and Services I",
            description: "Understanding different types of goods",
            slos: [
              "Define and give examples of private goods",
              "Define and give examples of public goods",
              "Define and give examples of mixed goods",
              "Define and give examples of merit goods",
              "Define and give examples of demerit goods"
            ]
          },
          {
            id: "ECO2.3",
            title: "Equality and Equity I",
            description: "Understanding economic inequality",
            slos: [
              "Define equality and equity",
              "Identify causes of economic inequalities",
              "Compare equality vs efficiency trade-offs",
              "Analyze positive and negative effects of inequality",
              "Evaluate policies to reduce inequality"
            ]
          }
        ],
        topics: [
          { name: "Role of Government", weeks: "1-3", description: "Economic functions, intervention reasons" },
          { name: "Government Revenue", weeks: "4-5", description: "Taxation types and systems" },
          { name: "Types of Goods", weeks: "6-7", description: "Public, private, merit, demerit goods" },
          { name: "Equity and Equality", weeks: "8-10", description: "Inequality causes and policies" }
        ]
      },
      {
        id: "y11-eco-3",
        name: "Money",
        code: "ECO3",
        weighting: 13,
        majorLearningOutcome: "Demonstrate understanding of the role of money in the economy",
        subStrands: [
          {
            id: "ECO3.1",
            title: "Introduction to Money I",
            description: "Understanding the nature and functions of money",
            slos: [
              "Describe the evolution of money from barter to modern forms",
              "Explain the four functions of money (medium of exchange, store of value, unit of account, standard of deferred payment)",
              "Identify the characteristics of good money",
              "Describe the role of financial institutions"
            ]
          },
          {
            id: "ECO3.2",
            title: "The Money Supply I",
            description: "Understanding money supply and its effects",
            slos: [
              "Define money supply and its components",
              "Explain the relationship between money supply and price levels (Quantity Theory)",
              "Apply Fisher's Equation (MV = PQ)",
              "Explain factors affecting money supply"
            ]
          },
          {
            id: "ECO3.3",
            title: "Inflation I",
            description: "Understanding inflation and its effects",
            slos: [
              "Define inflation and deflation",
              "Describe methods of measuring inflation (CPI)",
              "Calculate the rate of inflation",
              "Identify causes of inflation (demand-pull, cost-push)",
              "Analyze economic and social effects of inflation"
            ]
          }
        ],
        topics: [
          { name: "Evolution of Money", weeks: "1-2", description: "Barter, commodity money, modern money" },
          { name: "Functions of Money", weeks: "3-4", description: "Medium of exchange, store of value" },
          { name: "Money Supply", weeks: "5-6", description: "Components, Fisher's Equation" },
          { name: "Inflation", weeks: "7-9", description: "Causes, measurement, effects" }
        ]
      },
      {
        id: "y11-eco-4",
        name: "Trade and Payments",
        code: "ECO4",
        weighting: 12,
        majorLearningOutcome: "Demonstrate understanding of international trade and the balance of payments",
        subStrands: [
          {
            id: "ECO4.1",
            title: "Introduction to Trade and Trade Agreements I",
            description: "Understanding the basics of trade",
            slos: [
              "Define trade and explain reasons for trading",
              "Distinguish between domestic and international trade",
              "Explain the concept of trade agreements",
              "Identify Vanuatu's major exports and imports",
              "Analyze positive and negative impacts of trade"
            ]
          },
          {
            id: "ECO4.2",
            title: "Balance of Payments I",
            description: "Understanding the balance of payments",
            slos: [
              "Define balance of payments",
              "Distinguish between current account and capital account",
              "Explain foreign investment, foreign aid, and remittances",
              "Analyze the importance of these flows to Vanuatu"
            ]
          },
          {
            id: "ECO4.3",
            title: "Exchange Rate I",
            description: "Understanding exchange rates",
            slos: [
              "Define exchange rate",
              "Compare fixed vs floating exchange rates",
              "Identify factors affecting exchange rates",
              "Explain devaluation/depreciation and revaluation/appreciation",
              "Analyze the effects of exchange rate changes on trade"
            ]
          },
          {
            id: "ECO4.4",
            title: "Circular Flow of Income and Spending I",
            description: "Understanding macroeconomic flows",
            slos: [
              "Define aggregate demand and aggregate supply",
              "Define GDP and economic growth",
              "Describe causes and effects of economic growth",
              "Draw and explain the circular flow of income model",
              "Identify injections and withdrawals in the economy"
            ]
          }
        ],
        topics: [
          { name: "International Trade", weeks: "1-3", description: "Trade reasons, agreements, Vanuatu context" },
          { name: "Balance of Payments", weeks: "4-5", description: "Current and capital accounts" },
          { name: "Exchange Rates", weeks: "6-7", description: "Fixed vs floating, factors, effects" },
          { name: "Circular Flow", weeks: "8-10", description: "GDP, economic growth, injections/withdrawals" }
        ]
      }
    ]
  },

  "Year 12": {
    courseInfo: {
      subject: "Economics",
      level: "Year 12",
      school: "St Patrick's College",
      assessmentWeighting: {
        externalExamination: 60,
        internalAssessment: 40
      },
      totalWeeks: 40,
      periodsPerWeek: 4
    },
    strands: [
      {
        id: "y12-eco-1",
        name: "Resource Allocation through the Market System",
        code: "ECO1",
        weighting: 19,
        majorLearningOutcome: "Demonstrate advanced understanding of market resource allocation",
        subStrands: [
          {
            id: "ECO1.1",
            title: "Introduction to Economic Problems",
            description: "Advanced understanding of economic models and scarcity",
            slos: [
              "Explain the purpose of economic models",
              "Analyze economic scarcity and its implications",
              "Apply opportunity cost in decision making",
              "Explain production, economies and diseconomies of scale",
              "Analyze diminishing and increasing returns",
              "Interpret shifts in the Production Possibility Curve"
            ]
          },
          {
            id: "ECO1.2",
            title: "Consumer Demand",
            description: "Advanced demand analysis",
            slos: [
              "Apply the law of demand in various contexts",
              "Derive and analyze market demand curves",
              "Calculate price elasticity of demand using the mid-point formula",
              "Analyze factors affecting price elasticity",
              "Distinguish between elastic and inelastic demand"
            ]
          },
          {
            id: "ECO1.3",
            title: "Producers' Decisions and Supply",
            description: "Advanced supply analysis",
            slos: [
              "Apply the law of supply in various contexts",
              "Calculate price elasticity of supply",
              "Analyze total, average, and marginal costs",
              "Explain the relationship between production and costs",
              "Analyze effects of technology and investment on supply"
            ]
          },
          {
            id: "ECO1.4",
            title: "Market Equilibrium",
            description: "Advanced market analysis",
            slos: [
              "Analyze features of different market structures",
              "Determine equilibrium using supply and demand analysis",
              "Analyze disequilibrium situations",
              "Evaluate effects of government intervention",
              "Apply aggregate supply and demand concepts"
            ]
          }
        ],
        topics: [
          { name: "Economic Models", weeks: "1-3", description: "PPC analysis, production decisions" },
          { name: "Advanced Demand", weeks: "4-6", description: "Elasticity calculations, market demand" },
          { name: "Advanced Supply", weeks: "7-9", description: "Costs, elasticity, production" },
          { name: "Market Structures", weeks: "10-12", description: "Competition, equilibrium analysis" }
        ]
      },
      {
        id: "y12-eco-2",
        name: "Resource Allocation through the Public Sector",
        code: "ECO2",
        weighting: 16,
        majorLearningOutcome: "Demonstrate advanced understanding of government's role in resource allocation",
        subStrands: [
          {
            id: "ECO2.1",
            title: "Government Intervention II",
            description: "Advanced analysis of government policy",
            slos: [
              "Analyze government budget aims and components",
              "Evaluate different tax systems using principles of taxation",
              "Analyze government measures to address economic issues",
              "Explain monetary and fiscal policy tools"
            ]
          },
          {
            id: "ECO2.2",
            title: "Classifications of Goods and Services II",
            description: "Advanced analysis of goods types",
            slos: [
              "Analyze features of private, public, and mixed goods",
              "Evaluate policies to encourage merit good consumption",
              "Evaluate policies to discourage demerit good consumption"
            ]
          },
          {
            id: "ECO2.3",
            title: "Equality and Equity II",
            description: "Advanced equity analysis",
            slos: [
              "Distinguish between equity and equality",
              "Analyze positive and negative effects of inequality",
              "Evaluate effectiveness of policies to reduce inequality"
            ]
          }
        ],
        topics: [
          { name: "Government Budget", weeks: "1-3", description: "Revenue, expenditure, budget aims" },
          { name: "Taxation Principles", weeks: "4-5", description: "Efficiency, equity, simplicity" },
          { name: "Market Failure", weeks: "6-8", description: "Merit/demerit goods, externalities" },
          { name: "Income Distribution", weeks: "9-10", description: "Inequality measures and policies" }
        ]
      },
      {
        id: "y12-eco-3",
        name: "Money",
        code: "ECO3",
        weighting: 13,
        majorLearningOutcome: "Demonstrate advanced understanding of monetary economics",
        subStrands: [
          {
            id: "ECO3.1",
            title: "Introduction to Money II",
            description: "Advanced monetary concepts",
            slos: [
              "Analyze the functions of financial institutions",
              "Explain the roles of the Reserve Bank, commercial banks, and development banks",
              "Compare different financial institutions' functions"
            ]
          },
          {
            id: "ECO3.2",
            title: "The Money Supply II",
            description: "Advanced money supply analysis",
            slos: [
              "Analyze components of money supply",
              "Apply the Quantity Theory of Money",
              "Analyze effects of government borrowing on money supply",
              "Explain how commercial bank lending affects money supply"
            ]
          },
          {
            id: "ECO3.3",
            title: "Inflation II",
            description: "Advanced inflation analysis",
            slos: [
              "Analyze causes of demand-pull and cost-push inflation",
              "Evaluate social and economic effects of inflation",
              "Evaluate government policies to combat inflation"
            ]
          }
        ],
        topics: [
          { name: "Financial Institutions", weeks: "1-3", description: "Central bank, commercial banks" },
          { name: "Money Creation", weeks: "4-5", description: "Money multiplier, lending" },
          { name: "Inflation Management", weeks: "6-8", description: "Causes, effects, policies" }
        ]
      },
      {
        id: "y12-eco-4",
        name: "Trade and Payments",
        code: "ECO4",
        weighting: 12,
        majorLearningOutcome: "Demonstrate advanced understanding of international economics",
        subStrands: [
          {
            id: "ECO4.1",
            title: "Introduction to Trade and Trade Agreements II",
            description: "Advanced trade analysis",
            slos: [
              "Explain absolute and comparative advantage",
              "Analyze gains from trade and specialization",
              "Compare trade restriction vs free trade",
              "Explain terms of trade",
              "Analyze role of IMF and WTO"
            ]
          },
          {
            id: "ECO4.2",
            title: "Balance of Payments II",
            description: "Advanced balance of payments analysis",
            slos: [
              "Analyze components of current and capital accounts",
              "Explain balance of payments deficits and surpluses",
              "Analyze trends in Vanuatu's balance of payments"
            ]
          },
          {
            id: "ECO4.3",
            title: "Exchange Rate II",
            description: "Advanced exchange rate analysis",
            slos: [
              "Compare managed and flexible exchange rate systems",
              "Analyze effects of exchange rate changes on trade",
              "Evaluate exchange rate policies"
            ]
          },
          {
            id: "ECO4.4",
            title: "Circular Flow of Income and Spending II",
            description: "Advanced macroeconomic analysis",
            slos: [
              "Calculate and interpret GNI, GNE, and GDP per capita",
              "Distinguish between nominal and real GDP",
              "Analyze components of aggregate demand",
              "Analyze effects of unemployment on economic growth"
            ]
          }
        ],
        topics: [
          { name: "Trade Theory", weeks: "1-3", description: "Comparative advantage, specialization" },
          { name: "Balance of Payments", weeks: "4-6", description: "Accounts, deficits, surpluses" },
          { name: "Exchange Rates", weeks: "7-8", description: "Systems, policies, effects" },
          { name: "National Income", weeks: "9-10", description: "GDP, GNI, economic indicators" }
        ]
      }
    ]
  },

  "Year 13": {
    courseInfo: {
      subject: "Economics",
      level: "Year 13",
      school: "St Patrick's College",
      assessmentWeighting: {
        externalExamination: 60,
        internalAssessment: 40
      },
      totalWeeks: 40,
      periodsPerWeek: 4
    },
    strands: [
      {
        id: "y13-eco-1",
        name: "Resource Allocation through the Market System",
        code: "ECO1",
        weighting: 25,
        majorLearningOutcome: "Demonstrate comprehensive understanding of microeconomic theory",
        subStrands: [
          {
            id: "ECO1.1",
            title: "Economic Problem: Scarcity and Allocation",
            description: "Advanced microeconomic analysis",
            slos: [
              "Analyze productive and allocative efficiency",
              "Apply the Production Possibilities model",
              "Explain assumptions and implications of PPC shape",
              "Analyze increasing and constant cost PPCs",
              "Evaluate shifts in the PPC"
            ]
          },
          {
            id: "ECO1.2",
            title: "Demand, Utility and Elasticity",
            description: "Comprehensive demand analysis",
            slos: [
              "Apply marginal utility theory",
              "Analyze individual and market demand curves",
              "Calculate and interpret price elasticity of demand",
              "Analyze factors affecting elasticity",
              "Calculate cross-elasticity of demand",
              "Calculate income elasticity of demand",
              "Classify goods using elasticity measures"
            ]
          },
          {
            id: "ECO1.3",
            title: "Supply and Costs",
            description: "Comprehensive cost and supply analysis",
            slos: [
              "Distinguish between accounting and economic costs",
              "Analyze short-run average and marginal costs",
              "Apply the law of diminishing returns",
              "Determine shutdown point and break-even price",
              "Analyze consumer and producer surplus",
              "Calculate gains from trade",
              "Analyze welfare loss (deadweight loss)",
              "Evaluate incidence of taxes and subsidies"
            ]
          },
          {
            id: "ECO1.4",
            title: "Market Equilibrium, Market Structures and Analysis",
            description: "Advanced market structure analysis",
            slos: [
              "Analyze allocative efficiency in markets",
              "Evaluate effects of price ceilings and floors",
              "Analyze deadweight loss from market interventions",
              "Compare average, marginal, and total revenue",
              "Apply profit maximization rule (MR=MC)",
              "Analyze short-run and long-run equilibrium in different market structures"
            ]
          }
        ],
        topics: [
          { name: "Efficiency Concepts", weeks: "1-3", description: "Productive, allocative efficiency, PPC" },
          { name: "Utility and Elasticity", weeks: "4-7", description: "Marginal utility, all elasticity types" },
          { name: "Cost Analysis", weeks: "8-10", description: "Economic costs, surplus, welfare" },
          { name: "Market Structures", weeks: "11-14", description: "Perfect/imperfect competition, profit maximization" }
        ]
      },
      {
        id: "y13-eco-2",
        name: "Resource Allocation through the Public Sector",
        code: "ECO2",
        weighting: 15,
        majorLearningOutcome: "Demonstrate comprehensive understanding of market failure and government intervention",
        subStrands: [
          {
            id: "ECO2.1",
            title: "Government Intervention III",
            description: "Comprehensive market failure analysis",
            slos: [
              "Analyze causes and consequences of market failure",
              "Evaluate conditions for government intervention",
              "Analyze externalities in production and consumption",
              "Evaluate government policies for merit/demerit goods",
              "Analyze natural monopolies and economies of scale",
              "Compare social and private equilibrium"
            ]
          },
          {
            id: "ECO2.2",
            title: "Classification of Goods and Services III",
            description: "Advanced goods classification",
            slos: [
              "Analyze characteristics of pure public goods",
              "Evaluate relationships between goods and externalities"
            ]
          },
          {
            id: "ECO2.3",
            title: "Equity and Efficiency III",
            description: "Advanced equity analysis",
            slos: [
              "Analyze wealth and income distribution",
              "Interpret the Lorenz Curve",
              "Evaluate equity/efficiency trade-offs using PPF model",
              "Analyze factors causing deadweight loss"
            ]
          }
        ],
        topics: [
          { name: "Market Failure", weeks: "1-4", description: "Externalities, public goods" },
          { name: "Government Policy", weeks: "5-7", description: "Subsidies, taxes, regulations" },
          { name: "Distribution Analysis", weeks: "8-10", description: "Lorenz curve, equity/efficiency" }
        ]
      },
      {
        id: "y13-eco-3",
        name: "Money",
        code: "ECO3",
        weighting: 16,
        majorLearningOutcome: "Demonstrate comprehensive understanding of monetary and fiscal policy",
        subStrands: [
          {
            id: "ECO3.1",
            title: "Circular Flow of Income and Spending",
            description: "Government budget and fiscal policy",
            slos: [
              "Analyze government budget components",
              "Compare fiscal vs monetary policy",
              "Analyze fiscal constraints",
              "Evaluate impact of government revenue and expenditure"
            ]
          },
          {
            id: "ECO3.2",
            title: "Aggregate Supply and Demand Model",
            description: "Macroeconomic modeling",
            slos: [
              "Distinguish between real and nominal values",
              "Calculate nominal and real GDP using different approaches",
              "Analyze components of aggregate demand",
              "Explain factors shifting aggregate demand curve",
              "Analyze aggregate supply and its determinants",
              "Determine equilibrium price level, employment, and production",
              "Identify recessionary and inflationary gaps",
              "Analyze effects of monetary and fiscal policy"
            ]
          },
          {
            id: "ECO3.3",
            title: "Money, Interest Rates and Banking Systems",
            description: "Monetary policy and banking",
            slos: [
              "Analyze money market using supply and demand",
              "Explain the credit multiplier",
              "Evaluate monetary policy tools",
              "Analyze effects on money supply and interest rates",
              "Explain interest rate effects on consumption, investment, and net exports"
            ]
          }
        ],
        topics: [
          { name: "Fiscal Policy", weeks: "1-3", description: "Budget, taxation, government spending" },
          { name: "AD-AS Model", weeks: "4-8", description: "Aggregate demand, supply, equilibrium" },
          { name: "Monetary Policy", weeks: "9-12", description: "Money market, interest rates, credit" }
        ]
      },
      {
        id: "y13-eco-4",
        name: "Trade and Payments",
        code: "ECO4",
        weighting: 4,
        majorLearningOutcome: "Demonstrate comprehensive understanding of international economics",
        subStrands: [
          {
            id: "ECO4.1",
            title: "Balance of Payments and Foreign Exchange Rate",
            description: "Advanced international finance",
            slos: [
              "Analyze balance of payments accounts in detail",
              "Apply supply and demand analysis to foreign exchange market",
              "Analyze impact of exchange rate changes on exports and imports",
              "Evaluate factors influencing export and import demand",
              "Calculate and interpret terms of trade"
            ]
          },
          {
            id: "ECO4.2",
            title: "International Trade",
            description: "Advanced trade theory",
            slos: [
              "Apply comparative advantage theory",
              "Analyze gains from specialization and trade",
              "Evaluate trade restrictions vs free trade",
              "Analyze role of regional trade agreements, IMF, and WTO"
            ]
          }
        ],
        topics: [
          { name: "Foreign Exchange", weeks: "1-2", description: "Exchange markets, balance of payments" },
          { name: "Trade Policy", weeks: "3-4", description: "Free trade, protectionism, agreements" }
        ]
      }
    ]
  }
};

// Scheme of Work - Term by Term Planning
export const economicsSchemeOfWork = {
  "Year 11": {
    term1: [
      { week: "1-2", topic: "Introduction to Economics", outcomes: "Define scarcity, choice, opportunity cost", activities: "Case studies, PPC exercises" },
      { week: "3-4", topic: "Factors of Production", outcomes: "Identify factors and rewards", activities: "Local examples, group work" },
      { week: "5-6", topic: "The Law of Demand", outcomes: "Draw and interpret demand curves", activities: "Graphing exercises, market scenarios" },
      { week: "7-8", topic: "Determinants of Demand", outcomes: "Explain demand shifters", activities: "Real-world examples, analysis" },
      { week: "9-10", topic: "Price Elasticity of Demand", outcomes: "Calculate and interpret PED", activities: "Calculation exercises, case studies" }
    ],
    term2: [
      { week: "1-2", topic: "The Law of Supply", outcomes: "Draw and interpret supply curves", activities: "Graphing, production scenarios" },
      { week: "3-4", topic: "Determinants of Supply", outcomes: "Explain supply shifters", activities: "Business case studies" },
      { week: "5-6", topic: "Market Equilibrium", outcomes: "Determine equilibrium price and quantity", activities: "Supply-demand analysis" },
      { week: "7-8", topic: "Market Structures", outcomes: "Compare different market types", activities: "Market research, presentations" },
      { week: "9-10", topic: "Government Intervention", outcomes: "Analyze price controls and taxes", activities: "Policy analysis, debates" }
    ],
    term3: [
      { week: "1-2", topic: "Role of Government", outcomes: "Explain economic functions", activities: "Budget analysis, news articles" },
      { week: "3-4", topic: "Taxation", outcomes: "Compare tax systems", activities: "Tax calculations, case studies" },
      { week: "5-6", topic: "Types of Goods", outcomes: "Classify goods", activities: "Examples identification" },
      { week: "7-8", topic: "Money and Banking", outcomes: "Explain money functions", activities: "Banking simulation" },
      { week: "9-10", topic: "Money Supply", outcomes: "Apply Fisher's Equation", activities: "Calculation exercises" }
    ],
    term4: [
      { week: "1-2", topic: "Inflation", outcomes: "Calculate inflation rate, analyze causes", activities: "CPI analysis, data interpretation" },
      { week: "3-4", topic: "International Trade", outcomes: "Explain trade benefits", activities: "Vanuatu trade data analysis" },
      { week: "5-6", topic: "Balance of Payments", outcomes: "Analyze BOP components", activities: "Account analysis" },
      { week: "7-8", topic: "Exchange Rates", outcomes: "Explain exchange rate systems", activities: "Currency conversion exercises" },
      { week: "9-10", topic: "Revision and Examination", outcomes: "Review all strands", activities: "Past papers, practice tests" }
    ]
  },
  "Year 12": {
    term1: [
      { week: "1-3", topic: "Advanced Economic Models", outcomes: "Apply PPC analysis in depth", activities: "Advanced graphing, efficiency analysis" },
      { week: "4-6", topic: "Advanced Demand Analysis", outcomes: "Calculate elasticity using mid-point formula", activities: "Calculations, real data" },
      { week: "7-9", topic: "Cost Analysis", outcomes: "Analyze total, average, marginal costs", activities: "Cost calculations, business cases" },
      { week: "10", topic: "Assessment", outcomes: "Test on ECO1 concepts", activities: "Unit test, assignments" }
    ],
    term2: [
      { week: "1-3", topic: "Market Structures", outcomes: "Analyze different market forms", activities: "Case studies, market analysis" },
      { week: "4-6", topic: "Government Budget", outcomes: "Analyze budget components", activities: "Budget analysis, policy evaluation" },
      { week: "7-8", topic: "Taxation Principles", outcomes: "Apply principles of taxation", activities: "Tax policy analysis" },
      { week: "9-10", topic: "Market Failure", outcomes: "Analyze merit/demerit goods", activities: "Case studies, policy proposals" }
    ],
    term3: [
      { week: "1-3", topic: "Financial Institutions", outcomes: "Compare financial institutions", activities: "Banking research, presentations" },
      { week: "4-5", topic: "Money Creation", outcomes: "Explain credit creation", activities: "Multiplier calculations" },
      { week: "6-8", topic: "Inflation Management", outcomes: "Evaluate anti-inflation policies", activities: "Policy analysis, debates" },
      { week: "9-10", topic: "Assessment", outcomes: "Test on ECO2 and ECO3", activities: "Tests, research assignments" }
    ],
    term4: [
      { week: "1-3", topic: "Trade Theory", outcomes: "Apply comparative advantage", activities: "Trade calculations, analysis" },
      { week: "4-6", topic: "Balance of Payments", outcomes: "Analyze BOP accounts", activities: "Account analysis, trends" },
      { week: "7-8", topic: "National Income", outcomes: "Calculate GDP measures", activities: "Data analysis, calculations" },
      { week: "9-10", topic: "Revision and Examination", outcomes: "Review all strands", activities: "Past papers, exam prep" }
    ]
  },
  "Year 13": {
    term1: [
      { week: "1-3", topic: "Efficiency and PPC", outcomes: "Analyze productive/allocative efficiency", activities: "Advanced PPC analysis" },
      { week: "4-7", topic: "Utility and Elasticity", outcomes: "Calculate all elasticity types", activities: "Comprehensive calculations" },
      { week: "8-10", topic: "Cost and Supply Analysis", outcomes: "Apply cost theory", activities: "Business economics cases" }
    ],
    term2: [
      { week: "1-3", topic: "Market Structures", outcomes: "Compare market equilibria", activities: "Graph analysis, profit maximization" },
      { week: "4-6", topic: "Market Failure", outcomes: "Analyze externalities", activities: "Policy evaluation, case studies" },
      { week: "7-9", topic: "Government Intervention", outcomes: "Evaluate intervention tools", activities: "Policy analysis, debates" },
      { week: "10", topic: "Assessment", outcomes: "Test on ECO1 and ECO2", activities: "Major test, research project" }
    ],
    term3: [
      { week: "1-3", topic: "Fiscal Policy", outcomes: "Analyze budget policy", activities: "Fiscal policy simulation" },
      { week: "4-8", topic: "AD-AS Model", outcomes: "Apply macroeconomic analysis", activities: "Model building, equilibrium analysis" },
      { week: "9-10", topic: "Monetary Policy", outcomes: "Evaluate monetary tools", activities: "Money market analysis" }
    ],
    term4: [
      { week: "1-2", topic: "International Finance", outcomes: "Analyze exchange markets", activities: "Forex analysis" },
      { week: "3-4", topic: "Trade Policy", outcomes: "Evaluate trade policies", activities: "Policy debates" },
      { week: "5-8", topic: "Revision", outcomes: "Comprehensive review", activities: "Past papers, mock exams" },
      { week: "9-10", topic: "External Examination", outcomes: "Final preparation", activities: "Exam techniques" }
    ]
  }
};

// Yearly Planner
export const economicsYearlyPlanner = {
  "Year 11": {
    term1: {
      weeks: "Weeks 1-10",
      focus: "ECO1: Market System - Demand and Supply",
      units: [
        { title: "Introduction to Economics", weeks: "Week 1-2", strand: "ECO1", subStrand: "1.1", topics: ["Scarcity", "Choice", "Opportunity Cost"] },
        { title: "Factors of Production", weeks: "Week 3-4", strand: "ECO1", subStrand: "1.1", topics: ["Land", "Labor", "Capital", "Enterprise"] },
        { title: "Consumer Demand", weeks: "Week 5-8", strand: "ECO1", subStrand: "1.2", topics: ["Law of Demand", "Demand Curves", "Elasticity"] },
        { title: "Assessment Week", weeks: "Week 9-10", strand: "Assessment", subStrand: "-", topics: ["Unit Test 1", "Term Review"] }
      ],
      assessment: "Unit Test 1 + Continuous Assessment"
    },
    term2: {
      weeks: "Weeks 1-10",
      focus: "ECO1: Supply and Market Equilibrium",
      units: [
        { title: "Producer Supply", weeks: "Week 1-4", strand: "ECO1", subStrand: "1.3", topics: ["Law of Supply", "Supply Curves", "Elasticity"] },
        { title: "Market Equilibrium", weeks: "Week 5-7", strand: "ECO1", subStrand: "1.4", topics: ["Price Determination", "Market Structures"] },
        { title: "Government in Markets", weeks: "Week 8-10", strand: "ECO1", subStrand: "1.4", topics: ["Price Controls", "Taxes", "Subsidies"] }
      ],
      assessment: "Unit Test 2 + Project"
    },
    term3: {
      weeks: "Weeks 1-10",
      focus: "ECO2 & ECO3: Public Sector and Money",
      units: [
        { title: "Government Intervention", weeks: "Week 1-4", strand: "ECO2", subStrand: "2.1", topics: ["Government Functions", "Taxation"] },
        { title: "Types of Goods", weeks: "Week 5-6", strand: "ECO2", subStrand: "2.2", topics: ["Public", "Private", "Merit Goods"] },
        { title: "Money and Inflation", weeks: "Week 7-10", strand: "ECO3", subStrand: "3.1-3.3", topics: ["Money Functions", "Inflation"] }
      ],
      assessment: "Unit Test 3 + Research Assignment"
    },
    term4: {
      weeks: "Weeks 1-10",
      focus: "ECO4: Trade and Internal Examination",
      units: [
        { title: "International Trade", weeks: "Week 1-3", strand: "ECO4", subStrand: "4.1", topics: ["Trade Benefits", "Vanuatu Trade"] },
        { title: "Balance of Payments", weeks: "Week 4-5", strand: "ECO4", subStrand: "4.2", topics: ["Current Account", "Capital Account"] },
        { title: "Exchange Rates", weeks: "Week 6-7", strand: "ECO4", subStrand: "4.3", topics: ["Fixed vs Floating", "Effects"] },
        { title: "Revision & Exam", weeks: "Week 8-10", strand: "All", subStrand: "-", topics: ["Comprehensive Review"] }
      ],
      assessment: "Internal Examination (60%)"
    }
  },
  "Year 12": {
    term1: {
      weeks: "Weeks 1-10",
      focus: "ECO1: Advanced Microeconomics",
      units: [
        { title: "Economic Models", weeks: "Week 1-3", strand: "ECO1", subStrand: "1.1", topics: ["PPC Analysis", "Efficiency"] },
        { title: "Advanced Demand", weeks: "Week 4-6", strand: "ECO1", subStrand: "1.2", topics: ["Elasticity Calculations", "Mid-point Formula"] },
        { title: "Cost Analysis", weeks: "Week 7-10", strand: "ECO1", subStrand: "1.3", topics: ["TC, AC, MC", "Cost Curves"] }
      ],
      assessment: "Unit Test + Assignment"
    },
    term2: {
      weeks: "Weeks 1-10",
      focus: "ECO1 & ECO2: Markets and Government",
      units: [
        { title: "Market Structures", weeks: "Week 1-4", strand: "ECO1", subStrand: "1.4", topics: ["Competition Types", "Equilibrium"] },
        { title: "Government Policy", weeks: "Week 5-8", strand: "ECO2", subStrand: "2.1", topics: ["Budget", "Taxation"] },
        { title: "Market Failure", weeks: "Week 9-10", strand: "ECO2", subStrand: "2.2", topics: ["Externalities", "Public Goods"] }
      ],
      assessment: "Major Test + Research Project"
    },
    term3: {
      weeks: "Weeks 1-10",
      focus: "ECO3: Money and Banking",
      units: [
        { title: "Financial System", weeks: "Week 1-4", strand: "ECO3", subStrand: "3.1", topics: ["Banks", "Central Bank"] },
        { title: "Money Supply", weeks: "Week 5-7", strand: "ECO3", subStrand: "3.2", topics: ["Credit Creation", "Multiplier"] },
        { title: "Inflation Policy", weeks: "Week 8-10", strand: "ECO3", subStrand: "3.3", topics: ["Anti-inflation Measures"] }
      ],
      assessment: "Unit Test + Case Study"
    },
    term4: {
      weeks: "Weeks 1-10",
      focus: "ECO4: International Economics & Exam",
      units: [
        { title: "Trade Theory", weeks: "Week 1-3", strand: "ECO4", subStrand: "4.1", topics: ["Comparative Advantage"] },
        { title: "BOP & Exchange", weeks: "Week 4-6", strand: "ECO4", subStrand: "4.2-4.3", topics: ["Accounts", "Currency"] },
        { title: "National Income", weeks: "Week 7-8", strand: "ECO4", subStrand: "4.4", topics: ["GDP", "GNI"] },
        { title: "Revision & Exam", weeks: "Week 9-10", strand: "All", subStrand: "-", topics: ["External Exam Prep"] }
      ],
      assessment: "External Examination (60%)"
    }
  },
  "Year 13": {
    term1: {
      weeks: "Weeks 1-10",
      focus: "ECO1: Advanced Microeconomic Theory",
      units: [
        { title: "Efficiency Analysis", weeks: "Week 1-3", strand: "ECO1", subStrand: "1.1", topics: ["Productive", "Allocative Efficiency"] },
        { title: "Utility & Elasticity", weeks: "Week 4-7", strand: "ECO1", subStrand: "1.2", topics: ["Marginal Utility", "All Elasticities"] },
        { title: "Cost Theory", weeks: "Week 8-10", strand: "ECO1", subStrand: "1.3", topics: ["Economic Costs", "Surplus", "DWL"] }
      ],
      assessment: "Unit Test + Research Essay"
    },
    term2: {
      weeks: "Weeks 1-10",
      focus: "ECO1 & ECO2: Markets and Government",
      units: [
        { title: "Market Structures", weeks: "Week 1-4", strand: "ECO1", subStrand: "1.4", topics: ["Profit Maximization", "Competition"] },
        { title: "Market Failure", weeks: "Week 5-7", strand: "ECO2", subStrand: "2.1", topics: ["Externalities", "Public Goods"] },
        { title: "Equity Analysis", weeks: "Week 8-10", strand: "ECO2", subStrand: "2.3", topics: ["Lorenz Curve", "Distribution"] }
      ],
      assessment: "Major Test + Policy Analysis"
    },
    term3: {
      weeks: "Weeks 1-10",
      focus: "ECO3: Macroeconomic Policy",
      units: [
        { title: "Fiscal Policy", weeks: "Week 1-3", strand: "ECO3", subStrand: "3.1", topics: ["Budget", "Taxation"] },
        { title: "AD-AS Model", weeks: "Week 4-8", strand: "ECO3", subStrand: "3.2", topics: ["Equilibrium", "Gaps", "Policy"] },
        { title: "Monetary Policy", weeks: "Week 9-10", strand: "ECO3", subStrand: "3.3", topics: ["Interest Rates", "Money Market"] }
      ],
      assessment: "Major Test + Economic Report"
    },
    term4: {
      weeks: "Weeks 1-10",
      focus: "ECO4 & Final Preparation",
      units: [
        { title: "International Finance", weeks: "Week 1-2", strand: "ECO4", subStrand: "4.1", topics: ["Forex", "BOP"] },
        { title: "Trade Policy", weeks: "Week 3-4", strand: "ECO4", subStrand: "4.2", topics: ["Free Trade", "Protection"] },
        { title: "Comprehensive Revision", weeks: "Week 5-8", strand: "All", subStrand: "-", topics: ["All Strands Review"] },
        { title: "External Examination", weeks: "Week 9-10", strand: "Exam", subStrand: "-", topics: ["Final Exam"] }
      ],
      assessment: "External Examination (60%)"
    }
  }
};

export default economicsCurriculum;
